import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search, Tent, Store, BadgeCheck, Zap, Droplet, ArrowRight, Facebook, Instagram, Twitter, Youtube, HelpCircle, Users, Clock, ShieldCheck, CheckCircle2, ChevronRight, Globe, MousePointer2, LayoutDashboard, Image as ImageIcon, LogIn, Handshake } from 'lucide-react';
import { Logo } from '../components/Logo';
import { Footer } from '../components/Footer';
import { useTheme } from '../contexts/ThemeContext';
import { useEffect, useState } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'motion/react';
import { AuthModal } from '../components/AuthModal';
import { useAuth } from '../contexts/AuthContext';
import { isStandReferralCode, REFERRAL_WELCOME_DISCOUNT } from '../lib/referral';

// --- ANIMATED COUNTER COMPONENT ---
// `text` affiche une valeur statique (ex: "Bêta ouverte") au lieu d'animer un nombre —
// utilisé pour les statistiques qui ne sont pas des métriques chiffrées.
const Counter = ({ value, suffix = "", label, text }: { value?: number; suffix?: string; label: string; text?: string }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (text !== undefined || value === undefined) return;
    let start = 0;
    const end = value;
    const duration = 2000;
    const increment = end / (duration / 16);

    const timer = setInterval(() => {
      start += increment;
      if (start >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);

    return () => clearInterval(timer);
  }, [value, text]);

  return (
    <div className="text-center group">
      <div className="text-4xl md:text-5xl lg:text-7xl font-black text-white mb-2 group-hover:text-[#ff5a00] transition-colors duration-500">
        {text !== undefined ? text : <>{count.toLocaleString()}{suffix}</>}
      </div>
      <div className="text-[10px] md:text-xs font-black uppercase tracking-[0.2em] text-gray-500 group-hover:text-gray-300 transition-colors">{label}</div>
    </div>
  );
};

export default function LandingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { user } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'signup'>('signup');
  const [authRole, setAuthRole] = useState<'festival' | 'stand'>('festival');
  const [referralInvite, setReferralInvite] = useState(false);

  useEffect(() => {
    document.documentElement.classList.add('dark');
    document.documentElement.classList.remove('light');
    return () => {
      document.documentElement.classList.remove('dark', 'light');
      document.documentElement.classList.add(theme);
    };
  }, [theme]);

  // Parrainage exposant : un lien `?ref=STAND-XXXX` doit amener directement à
  // l'inscription (le code seul, capté en silence par App.tsx, ne servait à
  // rien tant que personne ne cliquait "Devenir exposant" de lui-même — trou
  // de conversion identifié le 20/07). On n'auto-ouvre que pour un visiteur
  // non connecté, une seule fois au chargement (pas de réouverture si fermée).
  useEffect(() => {
    if (user) return;
    const ref = new URLSearchParams(location.search).get('ref');
    if (ref && isStandReferralCode(ref)) {
      setReferralInvite(true);
      setAuthMode('signup');
      setAuthRole('stand');
      setShowAuthModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = (mode: 'login' | 'signup' = 'signup', role: 'festival' | 'stand' = 'festival') => {
    setAuthMode(mode);
    setAuthRole(role);
    setShowAuthModal(true);
  };

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col font-sans text-white overflow-x-hidden">
      {/* Header */}
      <header className="fixed top-0 left-0 w-full z-50 bg-black/80 backdrop-blur-xl border-b border-white/5 flex items-center justify-between px-6 py-4 md:px-10 h-20">
        <div className="flex items-center cursor-pointer" onClick={() => navigate('/')}>
          <Logo className="text-2xl md:text-3xl" />
        </div>
        
        <nav className="hidden lg:flex items-center space-x-10">
          <button className="text-[10px] font-black uppercase tracking-[0.3em] text-[#ff5a00] border-b-2 border-[#ff5a00] pb-1">{t('landing.navOrganizers')}</button>
          <button onClick={() => navigate('/exposants')} className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-500 hover:text-white transition-all pb-1 border-b-2 border-transparent">{t('landing.navExhibitors')}</button>
          <button onClick={() => navigate('/become-promoter')} className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-500 hover:text-white transition-all pb-1 border-b-2 border-transparent">{t('nav.becomePromoter')}</button>
        </nav>

        <div className="flex items-center space-x-3 md:space-x-4">
          <button
            onClick={() => handleLogin('login', 'festival')}
            className="hidden md:flex bg-[#ff5a00] hover:bg-[#ff6b1a] text-white font-black py-2 px-6 rounded-full text-xs uppercase tracking-widest transition-all shadow-xl shadow-[#ff5a00]/20 active:scale-95"
          >
            {t('landing.login')}
          </button>

          {/* Mobile Emoticons/Icons */}
          <div className="lg:hidden flex items-center space-x-2">
            <button
              onClick={() => navigate('/')}
              className={`p-3 transition-colors ${location.pathname === '/' ? 'text-[#ff5a00]' : 'text-gray-500 hover:text-white'}`}
              aria-label={t('landing.navOrganizers') as string}
            >
              <Tent className="w-6 h-6" />
            </button>
            <button
              onClick={() => navigate('/exposants')}
              className={`p-3 transition-colors ${location.pathname === '/exposants' ? 'text-[#ff5a00]' : 'text-gray-500 hover:text-white'}`}
              aria-label={t('landing.navExhibitors') as string}
            >
              <Store className="w-6 h-6" />
            </button>
            <button
              onClick={() => navigate('/become-promoter')}
              className="p-3 transition-colors text-gray-500 hover:text-white"
              aria-label={t('nav.becomePromoter') as string}
            >
              <Handshake className="w-6 h-6" />
            </button>
            <button
              onClick={() => handleLogin('login', 'festival')}
              className="ml-2 text-white bg-white/5 p-3 rounded-full border border-white/10 active:scale-95 transition-all"
              aria-label={t('landing.login') as string}
            >
              <LogIn className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 w-full pt-20">
        
        {/* HERO SECTION */}
        <section className="relative w-full min-h-[95vh] flex flex-col justify-center px-6 md:px-10 lg:px-20 overflow-hidden">
          {/* Animated Background Image from Screenshot Overlay */}
          <div 
            className="absolute inset-0 bg-cover bg-center z-0"
            style={{ backgroundImage: 'url("https://images.unsplash.com/photo-1459749411175-04bf5292ceea?q=80&w=2000&auto=format&fit=crop")' }}
          ></div>
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] to-transparent z-[1] opacity-70"></div>
          
          {/* Glowing Halos */}
          <div className="absolute top-1/4 right-[10%] w-[500px] h-[500px] bg-[#ff5a00]/20 rounded-full blur-[120px] pointer-events-none animate-pulse"></div>
          <div className="absolute bottom-1/4 left-[5%] w-[400px] h-[400px] bg-[#4b3cfa]/10 rounded-full blur-[100px] pointer-events-none"></div>
          
          <div className="relative z-10 max-w-[1400px] mx-auto w-full">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className="max-w-5xl"
            >
              <div className="inline-flex items-center space-x-2 px-5 py-2 rounded-full border border-white/10 bg-white/5 backdrop-blur-md text-[10px] font-black uppercase tracking-[0.3em] text-white mb-10 shadow-2xl">
                <div className="w-1.5 h-1.5 rounded-full bg-[#ff5a00] animate-ping mr-2"></div>
                {t('landing.badge')}
              </div>

              <h1 className="text-4xl md:text-7xl lg:text-8xl font-black tracking-tight leading-[0.9] mb-6 md:mb-10">
                {t('landing.heroTitle1')} <br className="hidden md:block" />
                {t('landing.heroTitle2')} <br className="hidden md:block" />
                <span className="text-[#4b3cfa] drop-shadow-[0_0_30px_rgba(75,60,250,0.3)]">{t('landing.heroFestivals')}</span> {t('landing.heroAnd')} <span className="text-[#ff5a00] drop-shadow-[0_0_30px_rgba(255,90,0,0.3)]">{t('landing.heroStands')}</span>
              </h1>

              <p className="text-base md:text-2xl text-gray-400 font-medium leading-relaxed max-w-2xl mb-8 md:mb-12 drop-shadow-lg">
                {t('landing.heroSubtitle')}
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 w-full max-w-2xl">
                {/* ORGANISATEUR CTA */}
                <button
                  onClick={() => handleLogin('signup', 'festival')}
                  className="group relative overflow-hidden bg-gradient-to-br from-[#ff5a00] to-[#ff4500] hover:from-[#ff6b1a] hover:to-[#ff5500] text-white font-black rounded-2xl md:rounded-[2rem] transition-all shadow-[0_20px_50px_rgba(255,90,0,0.3)] hover:shadow-[0_30px_70px_rgba(255,90,0,0.4)] active:scale-95 flex flex-col items-center justify-center py-6 md:py-8 px-6 md:px-8 border border-white/10"
                >
                  <div className="absolute inset-0 bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                  <div className="relative z-10 flex items-center justify-center mb-3">
                    <Tent className="w-6 h-6 md:w-8 md:h-8" />
                  </div>
                  <div className="relative z-10 text-center">
                    <div className="text-sm md:text-base font-black uppercase tracking-wider mb-1">{t('landing.ctaOrganizerTitle')}</div>
                    <div className="text-[11px] md:text-xs text-white/80 font-medium">{t('landing.ctaOrganizerSub')}</div>
                  </div>
                  <ArrowRight className="w-4 h-4 md:w-5 md:h-5 absolute right-5 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                </button>

                {/* EXPOSANT CTA */}
                <button
                  onClick={() => handleLogin('signup', 'stand')}
                  className="group relative overflow-hidden bg-white/[0.08] hover:bg-white/[0.12] backdrop-blur-xl border border-white/20 hover:border-white/40 text-white font-black rounded-2xl md:rounded-[2rem] transition-all shadow-[0_10px_30px_rgba(255,255,255,0.1)] hover:shadow-[0_20px_50px_rgba(255,255,255,0.15)] active:scale-95 flex flex-col items-center justify-center py-6 md:py-8 px-6 md:px-8"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                  <div className="relative z-10 flex items-center justify-center mb-3">
                    <Store className="w-6 h-6 md:w-8 md:h-8" />
                  </div>
                  <div className="relative z-10 text-center">
                    <div className="text-sm md:text-base font-black uppercase tracking-wider mb-1">{t('landing.ctaExhibitorTitle')}</div>
                    <div className="text-[11px] md:text-xs text-white/70 font-medium">{t('landing.ctaExhibitorSub')}</div>
                  </div>
                  <ArrowRight className="w-4 h-4 md:w-5 md:h-5 absolute right-5 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                </button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* SECTION CHIFFRES CLÉS */}
        <section className="py-16 md:py-24 relative bg-white/[0.01] border-y border-white/5 overflow-hidden">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-32">
              <Counter text={t('landing.statFestivalsValue')} label={t('landing.statFestivals')} />
              <Counter value={0} suffix="%" label={t('landing.statSatisfaction')} />
              <Counter value={100} suffix="%" label={t('landing.statTimeSaved')} />
            </div>
          </div>
        </section>

        {/* INFINITE MARQUEE */}
        <section className="py-12 md:py-20 bg-black/40 overflow-hidden relative">
          <div className="absolute inset-y-0 left-0 w-20 md:w-60 bg-gradient-to-r from-[#050505] to-transparent z-10 pointer-events-none"></div>
          <div className="absolute inset-y-0 right-0 w-20 md:w-60 bg-gradient-to-l from-[#050505] to-transparent z-10 pointer-events-none"></div>
          
          <div className="flex w-max animate-marquee whitespace-nowrap py-4 md:py-6 items-center">
            {["FESTIVAL", "FOIRE", "MARCHÉ", "POP-UP", "SALON", "GASTRONOMIE", "SPORT", "LOCALE"].map((cat, idx) => (
              <span key={idx} className="text-2xl md:text-6xl font-black text-white/5 hover:text-[#ff5a00]/40 transition-colors duration-700 mx-10 md:mx-20 select-none cursor-default uppercase italic tracking-tighter">
                {cat}
              </span>
            ))}
            {["FESTIVAL", "FOIRE", "MARCHÉ", "POP-UP", "SALON", "GASTRONOMIE", "SPORT", "LOCALE"].map((cat, idx) => (
              <span key={`clone-${idx}`} className="text-2xl md:text-6xl font-black text-white/5 hover:text-[#ff5a00]/40 transition-colors duration-700 mx-10 md:mx-20 select-none cursor-default uppercase italic tracking-tighter">
                {cat}
              </span>
            ))}
          </div>
        </section>

        {/* COMMENT ÇA MARCHE */}
        <section className="py-16 md:py-40 px-6 md:px-10 lg:px-20 relative">
          <div className="max-w-[1400px] mx-auto">
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 md:mb-32 gap-6 md:gap-12">
              <div className="max-w-2xl">
                <div className="text-[#ff5a00] font-black uppercase tracking-[0.3em] text-[10px] md:text-xs mb-4">{t('landing.processLabel')}</div>
                <h2 className="text-3xl md:text-6xl lg:text-7xl font-black leading-tight tracking-tighter">{t('landing.processTitle1')} <br /> {t('landing.processTitle2')}</h2>
              </div>
              <p className="text-lg md:text-xl text-gray-500 max-w-sm mb-4 font-medium leading-relaxed">
                {t('landing.processSubtitle')}
              </p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-10">
              {[
                {
                  step: 1,
                  title: t('landing.step1Title'),
                  desc: t('landing.step1Desc'),
                  icon: LayoutDashboard,
                  color: "from-blue-500/20 to-blue-600/5",
                  badge: "bg-blue-500/15 text-blue-400 border-blue-500/20"
                },
                {
                  step: 2,
                  title: t('landing.step2Title'),
                  desc: t('landing.step2Desc'),
                  icon: Users,
                  color: "from-[#ff5a00]/20 to-[#ff5a00]/5",
                  badge: "bg-[#ff5a00]/15 text-[#ff5a00] border-[#ff5a00]/20"
                },
                {
                  step: 3,
                  title: t('landing.step3Title'),
                  desc: t('landing.step3Desc'),
                  icon: Zap,
                  color: "from-emerald-500/20 to-emerald-600/5",
                  badge: "bg-emerald-500/15 text-emerald-400 border-emerald-500/20"
                }
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  whileHover={{ scale: 1.02, rotateY: 5 }}
                  transition={{ duration: 0.4 }}
                  className="bg-white/[0.03] rounded-3xl md:rounded-[3.5rem] p-6 md:p-12 border border-white/10 relative overflow-hidden group shadow-2xl h-full flex flex-col"
                >
                  <div className={`absolute -top-12 -right-12 w-48 h-48 bg-gradient-to-br ${item.color} rounded-full blur-3xl opacity-50 group-hover:opacity-100 transition-opacity`}></div>

                  <div className="absolute top-2 right-4 text-[6rem] md:text-[8rem] font-black text-white/[0.04] leading-none select-none pointer-events-none">
                    {item.step}
                  </div>

                  <div className={`relative w-12 h-12 md:w-16 md:h-16 rounded-xl md:rounded-2xl border flex items-center justify-center mb-6 md:mb-10 ${item.badge}`}>
                    <item.icon className="w-6 h-6 md:w-8 md:h-8" />
                  </div>

                  <div className="relative flex-1">
                    <h3 className="text-2xl md:text-3xl font-black mb-4 md:mb-6 leading-tight">{item.title}</h3>
                    <p className="text-sm md:text-base text-gray-500 leading-relaxed font-medium">{item.desc}</p>
                  </div>

                  <div className="relative mt-8 md:mt-12 text-[#ff5a00] opacity-100 md:opacity-0 group-hover:opacity-100 transition-all translate-x-0 md:translate-x-[-10px] md:group-hover:translate-x-0">
                    <ArrowRight className="w-6 h-6 md:w-8 md:h-8" />
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* NOS AVANTAGES */}
        <section className="py-16 md:py-40 px-6 md:px-10 lg:px-20 bg-white/[0.01] border-y border-white/5">
          <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-24 items-center">
            <div className="order-2 lg:order-1">
              <div className="relative group">
                <div className="absolute -inset-4 md:-inset-8 bg-gradient-to-r from-[#ff5a00]/30 to-[#4b3cfa]/30 rounded-3xl md:rounded-[4rem] blur-[60px] md:blur-[100px] opacity-40 animate-pulse"></div>
                <div className="relative bg-[#121212] border border-white/10 rounded-3xl md:rounded-[3.5rem] p-3 md:p-4 overflow-hidden shadow-2xl transform md:rotate-1 hover:rotate-0 transition-transform duration-700">
                  <div className="bg-[#050505] rounded-[2rem] md:rounded-[3rem] p-6 md:p-12">
                    <div className="flex items-center justify-between mb-8 md:mb-12">
                      <div className="flex items-center space-x-3">
                        <div className="w-6 h-6 md:w-8 md:h-8 rounded-lg bg-[#ff5a00] flex items-center justify-center">
                          <Zap className="w-3 h-3 md:w-4 md:h-4 text-white fill-white" />
                        </div>
                        <Logo className="h-4 md:h-6" />
                      </div>
                      <div className="text-[8px] md:text-[10px] font-black uppercase tracking-widest text-[#ff5a00] bg-[#ff5a00]/10 px-3 md:px-4 py-1.5 md:py-2 rounded-full border border-[#ff5a00]/20">{t('landing.mockupBadge')}</div>
                    </div>

                    <div className="flex items-center justify-between mb-5 md:mb-8">
                      <div className="text-sm md:text-lg font-black text-white">{t('landing.mockupTitle')}</div>
                      <div className="w-6 h-6 md:w-8 md:h-8 rounded-full bg-[#ff5a00] text-white text-[10px] md:text-xs font-black flex items-center justify-center">3</div>
                    </div>

                    <div className="space-y-2 md:space-y-3">
                      {[
                        { name: "L'Atelier Burger", cat: 'food_truck', status: t('landing.mockupPending'), color: 'text-amber-400 bg-amber-400/10 border-amber-400/20' },
                        { name: 'La Crêpe Dorée', cat: 'food_truck', status: t('landing.mockupAccepted'), color: 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20' },
                        { name: 'Fripes & Co', cat: 'clothes', status: t('landing.mockupAwaitingPayment'), color: 'text-blue-400 bg-blue-400/10 border-blue-400/20' },
                      ].map((row) => (
                        <div key={row.name} className="flex items-center justify-between gap-2 py-3 md:py-4 px-3 md:px-5 bg-white/5 rounded-xl md:rounded-2xl border border-white/5">
                          <div className="min-w-0">
                            <div className="text-[11px] md:text-sm font-bold text-white truncate">{row.name}</div>
                            <div className="text-[9px] md:text-[11px] font-bold uppercase tracking-wider text-blue-400/70 mt-0.5">{row.cat}</div>
                          </div>
                          <div className={`shrink-0 text-[8px] md:text-[10px] font-black uppercase tracking-wider px-2 md:px-3 py-1 md:py-1.5 rounded-full border whitespace-nowrap ${row.color}`}>{row.status}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="order-1 lg:order-2">
              <div className="text-[#ff5a00] font-black uppercase tracking-[0.3em] text-[10px] md:text-xs mb-6 md:mb-8">{t('landing.benefitsLabel')}</div>
              <h2 className="text-3xl md:text-6xl font-black mb-8 md:mb-12 tracking-tighter leading-tight italic text-center lg:text-left">
                {t('landing.benefitsTitle1')} <br /> {t('landing.benefitsTitle2')} <span className="text-transparent bg-clip-text bg-gradient-to-r from-white via-white/80 to-white/40">{t('landing.benefitsTitle3')}</span>
              </h2>

              <div className="grid grid-cols-1 gap-8 md:gap-12">
                {[
                  { icon: Clock, title: t('landing.adv1Title'), desc: t('landing.adv1Desc') },
                  { icon: BadgeCheck, title: t('landing.adv2Title'), desc: t('landing.adv2Desc') },
                  { icon: MousePointer2, title: t('landing.adv3Title'), desc: t('landing.adv3Desc') }
                ].map((adv, i) => (
                  <div key={i} className="flex flex-col md:flex-row items-center md:items-start text-center md:text-left space-y-4 md:space-y-0 md:space-x-8 group cursor-default">
                    <div className="w-12 h-12 md:w-14 md:h-14 bg-white/5 rounded-2xl flex items-center justify-center border border-white/10 group-hover:border-[#ff5a00]/30 transition-all shrink-0">
                      <adv.icon className="w-6 h-6 text-[#ff5a00]" />
                    </div>
                    <div>
                      <h4 className="text-lg md:text-2xl font-black mb-2 md:mb-3 group-hover:text-white transition-colors">{adv.title}</h4>
                      <p className="text-sm md:text-base text-gray-500 font-medium leading-relaxed max-w-lg">{adv.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA FINAL */}
        <section className="py-24 md:py-48 px-6 relative overflow-hidden bg-black">
          {/* Noise Texture logic or similar background could go here */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-gradient-to-b from-[#ff5a00]/10 to-transparent pointer-events-none opacity-40"></div>
          
          <div className="max-w-7xl mx-auto text-center relative z-10">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1 }}
            >
              <h2 className="text-4xl md:text-7xl lg:text-8xl font-black mb-8 md:mb-16 tracking-tighter leading-none italic uppercase">
                {t('landing.finalCtaTitle1')} <br /> <span className="text-[#ff5a00] drop-shadow-[0_0_50px_rgba(255,90,0,0.5)]">{t('landing.finalCtaTitle2')}</span>
              </h2>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-6 md:gap-10">
                <button
                  onClick={() => handleLogin('signup', 'festival')}
                  className="w-full sm:w-auto bg-[#ff5a00] hover:bg-[#ff6b1a] text-white font-black h-16 md:h-24 px-10 md:px-20 rounded-2xl md:rounded-[2.5rem] text-lg md:text-2xl transition-all shadow-[0_20px_80px_rgba(255,90,0,0.4)] active:scale-95 flex items-center justify-center group"
                >
                  {t('landing.launchFestival')}
                  <ArrowRight className="ml-4 w-6 h-6 md:w-8 md:h-8 group-hover:translate-x-2 transition-transform" />
                </button>
              </div>
              <div className="mt-12 md:mt-16 flex items-center justify-center space-x-6 md:space-x-12 opacity-40 grayscale">
                <div className="text-[10px] md:text-sm font-black tracking-widest uppercase">{t('landing.tagline')}</div>
              </div>
            </motion.div>
          </div>
        </section>

      </main>

      <Footer />

      {/* Floating Action Button */}

      {/* Floating Action Button */}
      <div className="fixed bottom-10 right-10 z-50">
        <button 
          onClick={() => navigate('/support')}
          className="bg-white/5 hover:bg-[#ff5a00] backdrop-blur-3xl text-white rounded-full w-16 h-16 border border-white/10 flex items-center justify-center shadow-2xl transition-all hover:scale-110 active:scale-90 group"
        >
          <HelpCircle className="w-7 h-7 group-hover:scale-110 transition-transform" />
        </button>
      </div>

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        initialMode={authMode}
        role={authRole}
        inviteNote={referralInvite ? t('landing.referralInvite', { amount: REFERRAL_WELCOME_DISCOUNT }) as string : undefined}
      />
    </div>
  );
}
